import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RequestWorkshopPick, SellableWorkshopDTO } from "../../../../models/EnrollmentHistoryDTO";
import { dinero, horaBonita } from "../workshops/comun";

/*
	Talleres y Personalizados en la Solicitud (Esteban, 2026-10-24). Se venden los talleres programados que no han terminado
	(Próximos y En Curso de Administración › Talleres y Personalizados): al aprobar, la alumna queda inscrita con su cobro
	único (sin mensualidades, recargos ni promociones) y su contrato de pago único. Cada taller lleva su nota de lo ofrecido.
	Precio distinto: solo roles de dinero, con motivo. Módulos Claros (2026-11-01): tarjetas del asistente (waOpcion), sin
	párrafo explicativo.
*/
export interface PasoTalleresProps {
	talleres: SellableWorkshopDTO[] | null;
	picks: RequestWorkshopPick[];
	onChange: (picks: RequestWorkshopPick[]) => void;
	busy: boolean;
	marcar: boolean;
	rolDinero: boolean;
	/** Los que ya se guardaron con un precio distinto (para enseñarlo aunque el taller ya no esté en la lista). */
	guardados?: { workshopId: number; name: string; priceByName?: string | null }[];
}

const fecha = (iso: string): string => {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
	return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
export const fechasTaller = (t: SellableWorkshopDTO): string =>
	t.startDate === t.endDate ? fecha(t.startDate) : `${fecha(t.startDate)} al ${fecha(t.endDate)}`;
/** "9:00 am - 1:00 pm" a partir de "09:00-13:00, 14:00-18:00". */
export const horasTaller = (t: SellableWorkshopDTO): string =>
	(t.timesText || "").split(",").map((x) => x.trim()).filter(Boolean)
		.map((x) => x.split("-").map((h) => horaBonita(h)).filter(Boolean).join(" - ")).join(", ");
/** "Taller Masajes" / "Taller de Uñas" sin repetir "Taller Taller de…" (VentaTallerService::conTaller). */
export const conTaller = (nombre: string): string => /^taller(es)?\b/i.test((nombre || "").trim()) ? nombre.trim() : "Taller " + (nombre || "").trim();
export const precioTaller = (t: SellableWorkshopDTO, v?: number | null): string => dinero({ symbol: t.currencySymbol } as any, v ?? t.price);

const vacio = (v: any) => v === null || v === undefined || String(v).trim() === "";

const PasoTalleres: React.FC<PasoTalleresProps> = ({ talleres, picks, onChange, busy, marcar, rolDinero, guardados }) => {
	const porId = (id: number) => (talleres || []).find((t) => t.id === id) || null;
	const elegido = (id: number) => picks.some((p) => p.workshopId === id);
	const toggle = (id: number) => onChange(elegido(id) ? picks.filter((p) => p.workshopId !== id) : [...picks, { workshopId: id, notes: "" }]);
	const cambiar = (id: number, campos: Partial<RequestWorkshopPick>) => onChange(picks.map((p) => p.workshopId === id ? { ...p, ...campos } : p));
	// Los elegidos que ya no están en la lista (guardados con otro estado) siguen saliendo abajo con su tarjeta.
	const lista = talleres || [];

	return (
		<div style={{ marginTop: 22 }}>
			<div className="waPasoSub">Talleres</div>
			{talleres === null ? <div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>Cargando…</div> : ""}
			{talleres !== null && talleres.length < 1 && picks.length < 1 ? <div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>Sin Talleres Programados</div> : ""}
			{lista.length ? (
				<div className="waOpciones" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
					{lista.map((t) => {
						const activa = elegido(t.id);
						const lleno = !activa && t.spotsLeft !== null && t.spotsLeft < 1;
						return (
							<button key={"tl_" + t.id} type="button" className={"waOpcion" + (activa ? " activa" : "")} aria-pressed={activa} disabled={busy || lleno} onClick={() => toggle(t.id)}
								style={{ minHeight: 0, padding: "12px 14px", gap: 4, textAlign: "center" }}>
								<span>{t.name}</span>
								<span style={{ fontSize: 13.5, fontWeight: 400, color: "var(--wa-apagado)" }}>
									{[fechasTaller(t), horasTaller(t)].filter(Boolean).join(", ")}
								</span>
								<span style={{ fontSize: 14 }}>{precioTaller(t)}{t.spotsLeft !== null ? <span style={{ fontWeight: 400, color: lleno ? "var(--wa-oro)" : "var(--wa-apagado)" }}> {lleno ? "Lleno" : t.spotsLeft + (t.spotsLeft === 1 ? " Lugar" : " Lugares")}</span> : ""}</span>
								{activa ? <FontAwesomeIcon icon={solid("circle-check")} className="waOpcionCheck" /> : ""}
							</button>
						);
					})}
				</div>
			) : ""}
			{picks.map((p) => {
				const t = porId(p.workshopId);
				const nombre = t ? t.name : (guardados || []).find((g) => g.workshopId === p.workshopId)?.name || `Taller #${p.workshopId}`;
				const sinNota = marcar && vacio(p.notes);
				const distinto = p.price !== null && p.price !== undefined;
				const sinMotivo = marcar && distinto && vacio(p.priceNote);
				const sinPrecio = marcar && distinto && !(Number(p.price) > 0);
				return (
					<div key={"tp_" + p.workshopId} className={"waTarjeta" + (sinNota || sinMotivo ? " waPasoFalta" : "")}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
							<div>
								<b>{nombre}</b>
								{t ? <div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>{[fechasTaller(t), t.durationText, horasTaller(t)].filter(Boolean).join(" · ")}</div> : ""}
								<div style={{ fontSize: 14 }}>
									Pago Único: <b>{t ? precioTaller(t, distinto ? Number(p.price) : null) : distinto ? String(p.price) : "-"}</b>
									{distinto && t ? <span style={{ color: "var(--wa-apagado)" }}> (el del taller es {precioTaller(t)})</span> : ""}
									{!distinto && t && t.priceStudent ? <span style={{ color: "var(--wa-apagado)" }}> · {precioTaller(t, t.priceStudent)} si ya está matriculado en un diplomado</span> : ""}
								</div>
							</div>
							<button type="button" className="wte-quitar" aria-label={"Quitar " + nombre} title="Quitar" disabled={busy} onClick={() => toggle(p.workshopId)}>×</button>
						</div>
						<textarea className="form-control" rows={2} placeholder="Lo Ofrecido" value={p.notes} disabled={busy}
							onChange={(e) => cambiar(p.workshopId, { notes: e.target.value })} />
						{sinNota ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>Nota de lo ofrecido · falta</div> : ""}
						{rolDinero ? (
							<div style={{ marginTop: 8 }}>
								<label style={{ position: "static", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
									<input type="checkbox" checked={distinto} disabled={busy}
										onChange={(e) => cambiar(p.workshopId, e.target.checked ? { price: t ? t.price : 0, priceNote: "" } : { price: null, priceNote: null })} />
									Cobrar un Precio Distinto
								</label>
								{distinto ? (
									<div className="row" style={{ marginTop: 4 }}>
										<div className="col-sm-4" style={{ marginBottom: 6 }}>
											<input className="form-control" type="number" min={0} step="0.01" value={p.price ?? ""} disabled={busy}
												placeholder="Precio" onChange={(e) => cambiar(p.workshopId, { price: e.target.value === "" ? 0 : Number(e.target.value) })} />
										</div>
										<div className="col-sm-8" style={{ marginBottom: 6 }}>
											<input className="form-control" maxLength={500} placeholder="Motivo (obligatorio)" value={p.priceNote || ""} disabled={busy}
												onChange={(e) => cambiar(p.workshopId, { priceNote: e.target.value })} />
										</div>
									</div>
								) : ""}
								{sinPrecio ? <div style={{ fontSize: 14, color: "var(--wa-oro)" }}>Precio · falta</div> : ""}
								{sinMotivo ? <div style={{ fontSize: 14, color: "var(--wa-oro)" }}>Motivo del precio · falta</div> : ""}
							</div>
						) : distinto ? (
							<div style={{ fontSize: 14, color: "var(--wa-apagado)", marginTop: 6 }}>
								Precio puesto por {(guardados || []).find((g) => g.workshopId === p.workshopId)?.priceByName || "Caja"}: {p.priceNote}
							</div>
						) : ""}
					</div>
				);
			})}
		</div>
	);
};

export default PasoTalleres;
